import * as React from "react";

import * as Rest from "../../Generated/RestClient";
import {clientAppSettings} from "../../Models/ClientAppSettings";
import {GenericDialog} from "../../Dialogs/GenericDialog";
import * as RejectionDetailsView from "./RejectionDetailsPanel";
import {ChannelLogPanelProps} from "../../Views/ChannelLogPanel";
import {ChannelEnabledPanelProps} from "../../Views/ChannelEnabledPanel";
import {BotTab} from "../../Views/BotTab";

import { Resources } from "../../Resources/Intercom.Resources.DevPortal";

export interface ChannelDataHandlers {
    showEmbedHandler: (_lastFocus: React.ReactInstance) => void;
    refreshChannelLogs: (bot: Rest.BotService) => void;
    channelStateChangeHandler: (column: string, id: string, isOn: boolean) => void;
    channelLogs: Rest.BotLog[];
    channelLogDisplayHandler: (channelLogDisplay: ChannelLogPanelProps) => void;
    channelEnabledDisplayHandler: (channelEnabledDisplay: ChannelEnabledPanelProps) => void;
    getChannelPublishStatus: (channelId: string) => Rest.ReviewStatusDetail;
}

export interface BotChannelsProps extends React.Props<BotChannels> {
    bot: Rest.BotService;
    userCanWriteBot: boolean;
    className?: string;
    channelDataHandlers: ChannelDataHandlers;
    channelConfigClickHandler?: (string) => void;
}

interface IssuePrompt {
    (issues: any[], template: string): string;
}

interface TryOnChannel {
    (channel: Rest.Channel, template: string): string;
}

export interface BotChannelsState {
}

// These channels should not be shown in the dev portal
// omnichannel and searchassistant only support the Ibiza react config page
// conversationconductor and conversationconductorivr doesn't have UI
// Skypeforbusiness, kik and kaizala are deprecated
export const hiddenChannels = [
    'omnichannel',
    'searchassistant',
    'skypeforbusiness',
    'kik',
    'kaizala',
    'conversationconductor',
    'conversationconductorivr'
]

// These channels are hidden in unconfigured channels list
export const hiddenChannelsForNew = [
    'telephony'
]

export class BotChannels extends React.Component<BotChannelsProps, BotChannelsState> implements BotTab {
    lastFocus?: React.ReactInstance;

    constructor(props) {
        super(props);
        this.state = {};
    }

    private statusClassNameForSeverity(sev: Rest.SeverityType): string {
        switch (sev) {
            case Rest.SeverityType.Error:
                return 'bot-status-color-error';
            case Rest.SeverityType.Warning:
                return 'bot-status-color-warning';
            default:
                return 'bot-status-color-ok';
        }
    }

    private getChannelLogEntries(channelId: string): Rest.BotLog[] {
        if (this.props.channelDataHandlers.channelLogs) {
            return this.props.channelDataHandlers.channelLogs.filter(entry => { return entry.ChannelId === channelId; });
        }
        return [];
    }

    private isChannelEnabled(channelId: string): boolean {
        return this.props.bot.EnabledChannels.indexOf(channelId) >= 0;
    }

    private _issuePrompt: IssuePrompt;
    private get issuePrompt(): IssuePrompt {
        if (!this._issuePrompt) {
            this._issuePrompt = (issues: any[], template: string) => {
                let issueTemplate = _.template(Resources.issuesTemplate);
                let warningTemplate = _.template(Resources.warningTemplate);
                switch (template) {
                    case Resources.issuesTemplate:
                        return issueTemplate(issues);
                    case Resources.warningTemplate:
                        return warningTemplate(issues);
                    default:
                        return issues ? Resources.running : '';
                }
            }
        }
        return this._issuePrompt;
    }

    private getChannelLogDisplay(channelId: string, channelName: string): ChannelLogPanelProps {

        let channelLog = this.getChannelLogEntries(channelId);

        let statusClassName = 'bot-status-color-ok';
        if (channelLog.length > 0) {
            let maxSeverityLogEntry = channelLog.reduce((prev, curr, i, arr) => { return prev.Severity > curr.Severity ? prev : curr; });
            statusClassName = this.statusClassNameForSeverity(maxSeverityLogEntry.Severity);
        }

        let issueTemplateName = Resources.running;
        switch (statusClassName) {
            case 'bot-status-color-warning':
                issueTemplateName = Resources.warningTemplate;
                break;
            case 'bot-status-color-error':
                issueTemplateName = Resources.issuesTemplate;
                break;
            default:
                break;
        }

        let title = this.issuePrompt(channelLog, issueTemplateName);

        return {
            botId: this.props.bot.Id,
            channelId: channelId,
            title: title,
            channelLog: channelLog,
            statusClassName: statusClassName,
            channelName: channelName,
            channelLogChangeHandler: () => { this.props.channelDataHandlers.refreshChannelLogs(this.props.bot); },
            dialog: null
        };
    }

    private getChannelLog(channelId: string, channelName: string): JSX.Element {

        var channelLogDisplay = this.getChannelLogDisplay(channelId, channelName);

        if (channelLogDisplay.channelLog.length > 0) {
            return (
                <button
                    className={`${channelLogDisplay.statusClassName} channel-td-display-block`}
                    data-channel-name={channelName}
                    onClick={() => this.props.channelDataHandlers.channelLogDisplayHandler(channelLogDisplay) }
                    >
                    {channelLogDisplay.title}
                </button>
            );
        }
        else {
            return (
                <span
                    className={channelLogDisplay.statusClassName}
                    data-channel-name={channelName}
                    data-status-classname={channelLogDisplay.statusClassName}
                    >
                    {channelLogDisplay.title}
                </span>
            );
        }
    }

    private getContact(channelId: string): Rest.ContactInfo {
        var contacts = this.props.bot.Contacts;
        for (var i = 0; i < contacts.length; i++) {
            if (contacts[i].ChannelId === channelId) {
                return contacts[i];
            }
        }

        return null;
    }

    public static getConfigUrl(botId: string, channelId: string): string {
        var path = clientAppSettings.applicationPath === '/' ? '' : clientAppSettings.applicationPath;
        return `${path}/bots/channels?id=${botId}&channelId=${channelId}`;
    }

    private getConfigLink(channel: Rest.Channel, innerElement: JSX.Element, labelledby: string): JSX.Element {
        if (this.props.bot.Id) {
            var path = clientAppSettings.applicationPath === '/' ? '' : clientAppSettings.applicationPath;
            var configUrl;
            var target;
            var handle;
            if (channel.DeveloperConfigurationUrl) {
                configUrl = `${path}/ConfigChannel.aspx?botId=${this.props.bot.Id}&channelId=${channel.Id}`;
                target = '_blank';
                handle = false;
            } else {
                configUrl = BotChannels.getConfigUrl(this.props.bot.Id, channel.Id);
                target = '_self';
                handle = true;
            }

            // disable the config link if user can't write the bot
            let disabled = !this.props.userCanWriteBot;
            // for v1 bot, skype channel is no longer editable
            // unless it has previously been configured successfully (based on the
            // presence of skype contact info)
            if (!disabled && this.props.bot.Version === "1.0" && channel.Id === "skype") {
                disabled = !this.getContact(channel.Id);
            }

            return <a data-channel-link-id={channel.Id} className="config-link-container" href={configUrl} target={target}
                key={`configLink${channel.Id}`}
                disabled={disabled}
                title={disabled ? "To add Skype please upgrade to V3" : null}
                aria-labelledby={labelledby + ` ${channel.Id}-config-link`}
                id={`${channel.Id}-config-link`}
                onClick={
                    x => {
                        if (disabled) {
                            x.preventDefault();
                        } else if (handle) {
                            x.preventDefault();
                            this.props.channelConfigClickHandler(channel.Id);
                        } else {
                            window.open(configUrl, target).focus();
                        }
                    }
                }
                >{innerElement}</a>;
        }

        return null;
    }

    private renderChannelPublishStatus(channel: Rest.Channel): JSX.Element {

        const reviewStatus = this.props.channelDataHandlers.getChannelPublishStatus(channel.Id);
        if (!reviewStatus) {
            return <span>--</span>;
        }

        let className = '';
        switch (reviewStatus.ReviewStatus) {
            case Rest.ReviewStatus.InReview:
                className = 'publish-in-review';
                break;
            case Rest.ReviewStatus.Rejected:
                className = 'publish-rejected';
                break;
            case Rest.ReviewStatus.NotSubmitted:
                className = '';
                break;
            case Rest.ReviewStatus.Approved:
                className = 'publish-approved';
                break;
        }

        if (reviewStatus.ReviewStatus === Rest.ReviewStatus.Rejected) {
            return (
                <div className="m-temp-popout-menu">
                    <span onClick={() => this.togglePublishPopoutVisibility()}>{Resources.channelRejected}
                        <span className="m-temp-popout-menu-icon m-icon-ChevronDown"></span>
                    </span>

                    <div className="m-temp-popout-content">
                        <span className="m-temp-popout-option" onClick={() => {
                            this.togglePublishPopoutVisibility();
                            this.showRejectionComments(reviewStatus.Comment);
                        }}>{Resources.channelMoreDetails}</span>

                        <span className="m-temp-popout-option" onClick={() => {
                            location.href = "/bots/channels?id=" + this.props.bot.Id + "&channelId=" + channel.Id;
                        }
                        }>{Resources.channelResubmit}</span>
                    </div>
                </div>
            );
        }

        return (
            <span className={className}>{Rest.ReviewStatus[reviewStatus.ReviewStatus]}</span>
        );
    }

    private showRejectionComments(comment: string): void {
        let dialog = new GenericDialog<RejectionDetailsView.RejectionDetailsPanelProps>(RejectionDetailsView.RejectionDetailsPanel, {
            message: comment
        }, { dialogClass: 'rejection-details-dialog' });
        dialog.showDialog();
    }

    private togglePublishPopoutVisibility() {
        let content = $(".m-temp-popout-content");

        if (content.hasClass("visible")) {
            content.removeClass("visible");
        } else {
            content.addClass("visible");
        }
    }

    private _tryOn: TryOnChannel;
    private get tryOn(): TryOnChannel {
        if (!this._tryOn) {
            this._tryOn = (channel: Rest.Channel, template: string) => {
                let channelTemplate = _.template(Resources.tryOnChannel);
                if (template === Resources.tryOnChannel) {
                    return channelTemplate(channel);
                } else {
                    return '';
                }
            }
        }
        return this._tryOn;
    }

    private renderConfiguredChannels(channels: Rest.Channel[]): JSX.Element {
        let configuredChannels: JSX.Element[] = [];

        channels.forEach(channel => {
            if (hiddenChannels.indexOf(channel.Id.toLowerCase()) >= 0) {
                return;
            }
            let contact = this.getContact(channel.Id);

            let contactHref = this.getContactHref(contact);
            let channelDisplayName = channel.Name;
            if (channel.Id.toLowerCase() === 'skypeforbusiness') {
                channelDisplayName = `${channelDisplayName} ${Resources.deprecatedChannel}`;
            }

            let anchorTitle = this.tryOn(channel, Resources.tryOnChannel);

            let configuredChannelMobile = (
                <div role="row" className="channel channel-row" key={channel.Id} data-channel-id={channel.Id}>
                    <div role="cell" className="channel-name">
                        <img src={this.getChannelIconUrl(channel)} alt={channel.Name} title={channel.Name} className='channel-icon icon channel-test-icon' />
                        {
                            contactHref
                                ? <a className="channel-test channel-test-anchor" target="_blank" href={this.getContactHref(contact) } alt="Name" title={anchorTitle}>{channelDisplayName}</a>
                                : <span className="channel-test">{channelDisplayName}</span>
                        }
                    </div>
                        {
                            this.isChannelEnabled(channel.Id)
                                ? (<div role="cell" alt="Health">{this.getChannelLog(channel.Id, channel.Name)}</div>)
                                : (<span role="cell" className="channel-health" alt="Health">--</span>)
                        }
                    <div role="cell" className="channel-publish" alt={Resources.channelPublished}>{this.renderChannelPublishStatus(channel)}</div>
                    <div  role="cell">
                        {
                            this.props.userCanWriteBot ? this.getConfigLink(channel, (
                                <span className="config-link">
                                    <span className="config-link-text">{ Resources.channelEdit } <span className="offscreen">{ channel.Name }</span></span>
                                    <span aria-hidden="true" className="icon-font config-link-icon">&#xE104;</span>
                                </span>
                            ), "") : null
                        }
                    </div>
                </div>
            );

            // Only add other channels if bot is not isolated or the channel is Direct Line
            if ( this.props.bot.IsIsolated !== true || channel.Id.toLowerCase() === 'directline') {
                configuredChannels.push(configuredChannelMobile);
            }
        });

        if (configuredChannels.length > 0) {
            return (
                <div role="table">
                    <div role="rowgroup" className="channels-configured">
                        <div role="row"  className="channel-row channel-header">
                            <label role="columnheader">{Resources.channelname}</label>
                            <label role="columnheader">{Resources.health}</label>
                            <label role="columnheader">{Resources.published}</label>
                            <label role="columnheader">{Resources.actions}</label>
                        </div>
                    </div>
                    <div role="rowgroup">
                        {configuredChannels}
                    </div>
                </div>
            );
        }

        return (
            <div className="channel-message">
                {Resources.NoChannelsConfigured}
            </div>
            );
    }

    private renderUnconfiguredChannels(channels: Rest.Channel[]): JSX.Element {
        if (channels.length === 0) {
            return null;
        }

        // Only show the Direct Line channel if the bot is isolated to disable adding other channels.
        const featuredChannelsNames  = this.props.bot.IsIsolated === true ? ["directline"] : ["directline", "msteams", "webchat", "searchassistant"];

        const featuredChannels: JSX.Element[] = [];
        const otherChannels: JSX.Element[] = [];
        channels.forEach(channel => {
            if (hiddenChannels.indexOf(channel.Id.toLowerCase()) >= 0) {
                return;
            }
            if (hiddenChannelsForNew.indexOf(channel.Id.toLowerCase()) >= 0) {
                return;
            }
            let channelDisplayName = channel.Name;
            // Featured channels:
            if (featuredChannelsNames.indexOf(channel.Id) > -1) {
                const iconLabelName: string = channel.Id + "-icon-label";
                const tile: JSX.Element = (
                    <div className="add-channel-tile" key={`newChannel${channel.Id}`} alt={Resources.altImageChannel.replace('%1', channelDisplayName)}>
                        <img aria-labelledby={iconLabelName} src={ this.getChannelIconUrl(channel) } />
                        <span id={iconLabelName} className="offscreen">{ channelDisplayName }</span>
                    </div>
                );
                const addLink: JSX.Element = this.getConfigLink(channel, tile, "featured-channel-header");
                if (addLink) {
                    featuredChannels.push(addLink);
                }
            // Regular, unconfigured channels:
            } else if (this.props.bot.IsIsolated !== true) {
                const iconLabelName: string = channel.Id + "-icon-label";
                const tile: JSX.Element = (
                    <div className="small-add-channel-tile" key={`newChannel${channel.Id}`} alt={Resources.altImageChannel.replace('%1', channelDisplayName)}>
                        <img aria-labelledby={iconLabelName} src={ this.getChannelIconUrl(channel) } />
                        <span id={iconLabelName}>{channelDisplayName}</span>
                    </div>
                );
                const addLink: JSX.Element = this.getConfigLink(channel, tile, "more-channels-header");
                if (addLink) {
                    otherChannels.push(addLink);
                }
            }
        });

        if ((featuredChannels.length + otherChannels.length > 0) && this.props.bot.Version !== '1.0') {
            let featuredChannelsHeader: JSX.Element = null;
            let featuredChannelsElement: JSX.Element = null;
            if (featuredChannels.length > 0) {
                featuredChannelsHeader = (<h4 id="featured-channel-header">{Resources.AddAFeaturedChannelTitle}</h4>);
                featuredChannelsElement = (
                    <div className="flex-layout-grid featured-channels">
                        {featuredChannels}
                    </div>
                );
            }
            let otherChannelsHeader: JSX.Element =  null;
            let otherChannelsElement: JSX.Element = null;
            if (otherChannels.length > 0 && this.props.bot.IsIsolated !== true) {
                otherChannelsHeader = (<h4 id="more-channels-header">{Resources.MoreChannelsTitle}</h4>);
                otherChannelsElement = (
                    <div className="flex-layout-grid other-channels">
                        {otherChannels}
                    </div>
                );
            }
            return (
                <div className="add-channels">
                    {featuredChannelsHeader}
                    {featuredChannelsElement}
                    {otherChannelsHeader}
                    {otherChannelsElement}
                </div>
            );
        }
        return null;
    }

    private _tempDOMElement: HTMLDivElement;

    private get tempDOMElement(): HTMLDivElement {
        if (!this._tempDOMElement) {
            this._tempDOMElement = document.createElement("div");
        }
        return this._tempDOMElement;
    }

    private  getContactHtml(contact: Rest.ContactInfo) : string {
        if (contact && contact.HtmlLink) {

            this.tempDOMElement.innerHTML = contact.HtmlLink;
            var aa = this.tempDOMElement.getElementsByTagName('a');
            if (aa) {
                var a = aa[0] as HTMLAnchorElement;
                var href = a.href;
                if (a.href.indexOf('http') === 0) {
                    a.target = '_blank';
                }
                return this.tempDOMElement.innerHTML;
            }
        }
        return '';
    }

    private getContactHref(contact: Rest.ContactInfo): string {
        const contactHtml = this.getContactHtml(contact);
        if (contactHtml) {
            return $(this.tempDOMElement).find('a').attr('href');
        } else {
            return '';
        }
    }

    private getChannelIconUrl(channel: Rest.Channel): string {
        return channel.IconUrl;
    }

    public render(): JSX.Element {
        const allChannels = clientAppSettings.channels;
        const configuredChannelIds = this.props.bot.ConfiguredChannels;
        const configuredChannelInfos = allChannels.filter(channel => configuredChannelIds.indexOf(channel.Id) >= 0);
        const unconfiguredChannelInfos = allChannels.filter(channel => configuredChannelIds.indexOf(channel.Id) < 0);

        let embedCodeLink = null;
        // embedCode is retrieved via POST which implies write access
        if (configuredChannelInfos.length > 0 && this.props.userCanWriteBot  && this.props.bot.IsIsolated !== true) {
            embedCodeLink = <div className="get-embed-code">
                                <button className="get-embed-code-button button-focus" ref={(c => this.lastFocus = c)} onClick={() => {this.props.channelDataHandlers.showEmbedHandler(this.lastFocus);}}>{Resources.channelGetEmbedCodes}
                                </button>
                            </div>;
        }

        return (
            <div className={`bot-card grid-card channels ${this.props.className || ""}`}>
                <div><h1 className="page-header">{Resources.ConnectToChannelsTitle}</h1></div>
                {this.renderConfiguredChannels(configuredChannelInfos)}
                {embedCodeLink}

                {
                    this.props.userCanWriteBot
                    ? this.renderUnconfiguredChannels(unconfiguredChannelInfos)
                    :null
                }
            </div>
        );
    }

    public setBotId(botId: string) {
        // do nothing
    }

    public setBot(bot: Rest.BotService) {
        this.setState(_.extend({}, this.state, { bot: bot }));
    }
}



// WEBPACK FOOTER //
// ./Client/v4/Views/BotChannels.tsx